import { useEffect, useRef, useState } from "react";
import {
  DatePicker,
  INPUT_ICON_POSITIONS,
  IconSchedule,
  IconToday,
  MaskableFormattedInput,
  SecondaryButton,
  TextButton,
  Input,
} from "@cdkglobal/radial";

import moment, { Moment } from "moment";
import {
  ButtonRow,
  PickerRow,
  MegaPickerContainer,
  InputAndPickerContainer,
  DatePickerPositionWrapper,
} from "../../../../utils/styled";
import {
  DatePickerInputProps,
  RenderMegaDateTimePickerProps,
  RowDataValueType,
} from "../../../../types";
import { getCustomBlockedCalendarDates } from "../../evaluations/customDateEvaluations/customDateEvaluations";

const DatePickerInput = ({
  formattedId,
  date,
  dateFormat,
  handleDateChange,
  isDisabled,
  error,
  evalMethod,
  position,
}: Readonly<DatePickerInputProps>) => {
  return (
    <DatePickerPositionWrapper $position={position}>
      <DatePicker
        blockedDateRanges={
          evalMethod ? getCustomBlockedCalendarDates(evalMethod) : []
        }
        dataTestId="date-picker-input"
        date={date}
        dateFormat={dateFormat}
        dateInputProps={{
          helperText: dateFormat,
          isDisabled: isDisabled,
          errorMessage: "Enter a valid date",
          hasError: error,
          onError: () => {
            //TO DO
          },
        }}
        id={`${formattedId}-date-input`}
        label="Date"
        name="date-picker-simple"
        numberOfMonths={1}
        onDateChange={handleDateChange}
      />
    </DatePickerPositionWrapper>
  );
};

const TimePickerInput = ({
  hasError,
  isDisabled,
  formattedId,
  onBlur,
  onChange,
  value,
}: {
  hasError: boolean;
  isDisabled: boolean;
  formattedId: string;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value: string;
}) => {
  return (
    <div>
      <MaskableFormattedInput
        enableCustomValidation
        errorMessage="Enter a valid time"
        hasError={hasError}
        helperText="HH:MM:SS"
        icon={<IconSchedule />}
        iconPosition={INPUT_ICON_POSITIONS.LEADING}
        id={`${formattedId}-time-input`}
        dataTestId={"time-picker"}
        isDisabled={isDisabled}
        label="Time"
        mask="99:99:99"
        name="maskable-input-with-leading-icon"
        onBlur={onBlur}
        onChange={onChange}
        placeholder="00:00:00"
        value={value}
      />
    </div>
  );
};

const RenderMegaDateTimePicker = ({
  title,
  propertyKey,
  rowIndex,
  propertySubKey,
  inputValue,
  isRequired,
  error,
  message,
  isDialogField,
  isDisabled,
  evalMethod,
  selectedTabSectionKey,
  dispatch,
}: RenderMegaDateTimePickerProps): JSX.Element => {
  const fullValidTimePattern = "^([01]\\d|2[0-3]):([0-5]\\d):([0-5]\\d)$";
  const inputRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const shouldOpenUpward = Boolean(
    inputRef.current &&
      window.innerHeight - inputRef.current.getBoundingClientRect().bottom <
        300,
  );
  const parseDateAndTime = (inputValue: RowDataValueType) => {
    if (typeof inputValue !== "string") {
      return { startDate: null, startTime: "" };
    }

    const [date, time] = inputValue.split(" ");

    return {
      startDate: moment(date, "YYYY-MM-DD", true).isValid()
        ? moment(date, "YYYY-MM-DD", true)
        : null,
      startTime: new RegExp(fullValidTimePattern).test(time) ? time : "",
    };
  };

  const initialState = inputValue
    ? parseDateAndTime(inputValue)
    : {
        startDate: null,
        startTime: "",
      };

  const [dateTimePicker, setDateTimePicker] = useState({
    previousState: initialState,
    startDate: initialState.startDate,
    startTime: initialState.startTime,
    isDateValid: Boolean(initialState.startDate),
    isVisible: false,
    isSelected: false,
    chipLabel: inputValue ?? "Select Date & Time",
    hasDropdown: true,
  });
  const [startTimeHasError, setStartTimeHasError] = useState(false);

  const megaPickerDateFormat = "YYYY-MM-DD";
  const isValidTime = (value: string) =>
    new RegExp(fullValidTimePattern).test(value);

  const handleStartTimeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setStartTimeHasError(false);
    setDateTimePicker((ps) => ({
      ...ps,
      startTime: (event.target as HTMLInputElement).value,
    }));
    if ((event.target as HTMLInputElement).value.length === 8) {
      setStartTimeHasError(
        !isValidTime((event.target as HTMLInputElement).value),
      );
    }
  };

  const handleStartTimeBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setStartTimeHasError(
      !isValidTime((event.target as HTMLInputElement).value),
    );
  };
  const handleStartDateChange = (
    momentDateObject: Moment | null | undefined,
  ) => {
    if (!momentDateObject) {
      return;
    }

    if (momentDateObject) {
      setDateTimePicker((ps) => ({
        ...ps,
        isDateValid: momentDateObject.isValid(),
        startDate: momentDateObject,
        startTime: "00:00:00",
      }));
      setStartTimeHasError(false);
    } else {
      setDateTimePicker((ps) => ({
        ...ps,
        startDate: null,
        isDateValid: false,
      }));
    }
  };
  const handleOnClick = () => {
    if (!dateTimePicker.isVisible) {
      setDateTimePicker((ps) => ({
        ...ps,
        isVisible: true,
        isSelected: true,
        hasDropdown: false,
      }));
    }
  };

  const handleRemove = () => {
    setDateTimePicker((ps) => ({
      ...ps,
      previousState: initialState,
      startDate: initialState.startDate,
      startTime: initialState.startTime,
      isVisible: false,
      isSelected: false,
      chipLabel: "Select Date & Time",
      hasDropdown: true,
    }));
    dispatch({
      type: "UPDATE_FIELD",
      payload: {
        propertyKey,
        newFieldValue: null,
        rowIndex,
        propertySubKey,
        selectedTabSectionKey,
        isDialogField,
      },
    });
  };

  const handleOK = () => {
    const formattedStartDate = moment(dateTimePicker.startDate).format(
      megaPickerDateFormat,
    );
    setDateTimePicker((ps) => ({
      ...ps,
      previousState: {
        startDate: dateTimePicker.startDate,
        startTime: dateTimePicker.startTime,
      },
      isVisible: false,
      isSelected: true,
      chipLabel: `${formattedStartDate} ${dateTimePicker.startTime}`,
      hasDropdown: false,
    }));
    const newFieldValue = `${formattedStartDate} ${dateTimePicker.startTime}`;
    dispatch({
      type: "UPDATE_FIELD",
      payload: {
        propertyKey,
        newFieldValue,
        propertySubKey,
        rowIndex,
        selectedTabSectionKey,
        isDialogField,
      },
    });
  };

  const handleCancel = () => {
    const { previousState } = dateTimePicker;
    const isInitialState =
      previousState.startDate === initialState.startDate &&
      previousState.startTime === initialState.startTime;

    setDateTimePicker((ps) => ({
      ...ps,
      isSelected: !isInitialState,
      hasDropdown: isInitialState,
      startDate: previousState.startDate,
      startTime: previousState.startTime,
      isVisible: false,
    }));
  };

  const handleClose = () => {
    setDateTimePicker((ps) => ({
      ...ps,
      isVisible: false,
      isSelected: true,
      hasDropdown: false,
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        handleClose();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (inputValue !== dateTimePicker.chipLabel) {
      const newState = parseDateAndTime(inputValue);
      setDateTimePicker((ps) => ({
        ...ps,
        previousState: newState,
        startDate: newState.startDate,
        startTime: newState.startTime,
        isDateValid: Boolean(newState.startDate),
        chipLabel: inputValue ?? "Select Date & Time",
      }));
    }
  }, [inputValue]);

  const id =
    rowIndex !== undefined && propertySubKey
      ? `-${rowIndex}-${propertySubKey}`
      : "";
  const formattedId = `${propertyKey}${id}-dateTime`;

  return (
    <InputAndPickerContainer>
      <Input
        dataTestId={formattedId}
        icon={<IconToday />}
        id={formattedId}
        name="date-time-picker"
        label={title}
        onClick={handleOnClick}
        placeholder={"YYYY-MM-DD HH:mm:ss"}
        hasClearButton={inputValue !== "" && !isDisabled}
        iconPosition={INPUT_ICON_POSITIONS.LEADING}
        onInputClear={handleRemove}
        onChange={handleOnClick}
        isRequired={isRequired}
        isDisabled={isDisabled}
        value={inputValue ?? ""}
        enableCustomValidation={true}
        hasError={error}
        errorMessage={message}
        ref={inputRef}
      />
      {dateTimePicker.isVisible && (
        <MegaPickerContainer ref={pickerRef} $position={shouldOpenUpward ? "top" : "bottom"}>
          {title}
          <PickerRow>
            <DatePickerInput
              date={dateTimePicker.startDate ? dateTimePicker.startDate : null}
              dateFormat={megaPickerDateFormat}
              handleDateChange={handleStartDateChange}
              formattedId={formattedId}
              isDisabled={isDisabled}
              error={!dateTimePicker.isDateValid}
              evalMethod={evalMethod}
              position={shouldOpenUpward ? "top" : "bottom"}
            />
            <TimePickerInput
              hasError={startTimeHasError}
              isDisabled={!dateTimePicker.startDate}
              formattedId={formattedId}
              onBlur={handleStartTimeBlur}
              onChange={handleStartTimeChange}
              value={dateTimePicker.startTime}
            />
          </PickerRow>
          <ButtonRow>
            <TextButton
              dataTestId="picker-cancel-button"
              iconColor="primary"
              onClick={handleCancel}
              text="Cancel"
            />
            <SecondaryButton
              dataTestId="picker-ok-button"
              iconColor="primary"
              isDisabled={!dateTimePicker.isDateValid || startTimeHasError}
              onClick={handleOK}
              text="OK"
            />
          </ButtonRow>
        </MegaPickerContainer>
      )}
    </InputAndPickerContainer>
  );
};

export { RenderMegaDateTimePicker };
